import { Button, Collapse } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlayerPlayFilled } from '@tabler/icons-react';
import { motion } from 'motion/react';
import type React from 'react';

import { PropertyTable } from '#core/components/layout/PropertyTable.tsx';

import { DefaultStatus } from './DefaultStatus.tsx';
import classes from './ToolDisplay.module.css';
import { useRerunTool } from '#plugin-chat/hooks/useRerunTool.ts';
import { useToolPart } from '#plugin-chat/hooks/useToolPart.ts';

export interface ToolStatusProps {
  children: Exclude<React.ReactNode, boolean | null | undefined>[];
  /** Show default status when no children are provided */
  showDefault?: boolean;
}

export function ToolStatus({ children, showDefault = false, ..._props }: ToolStatusProps) {
  const [opened, { toggle }] = useDisclosure(false);
  const { args, result, toolName } = useToolPart();
  const rerun = useRerunTool();
  const response = { input: args, output: result };

  const hasChildren = children?.length > 0;

  if (!hasChildren && !showDefault) {
    return null;
  }

  // Dev-only re-run affordance. Re-executes the tool with its original args
  // and dumps input/output to the browser console — does not mutate chat
  // history. Shift-click invalidates the producer dataset cache first so a
  // producer tool actually re-fetches instead of returning cached data.
  const canRerun = import.meta.env.DEV && !!toolName && !!args;
  const handleRerun = (event: React.MouseEvent) => {
    if (!toolName || !args) {
      return;
    }
    void rerun(toolName, args as Record<string, unknown>, { force: event.shiftKey });
  };

  return (
    <motion.div
      animate={{ opacity: 1, x: 0 }}
      className={classes.status}
      initial={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      {hasChildren ? children : <DefaultStatus />}

      {!!result && (
        <>
          <div className={classes.actions}>
            {canRerun && (
              <Button
                color="dark.3"
                fw="normal"
                h="lg"
                leftSection={<IconPlayerPlayFilled size={12} />}
                onClick={handleRerun}
                radius="xl"
                size="xs"
                title="Re-run tool with original args (logs to console). Shift-click to bypass dataset cache."
                variant="outline"
              >
                Run
              </Button>
            )}
            <Button color="dark.3" fw="normal" h="lg" onClick={toggle} radius="xl" size="xs" variant="outline">
              Show details
            </Button>
          </div>

          <Collapse className={classes.responseObject} expanded={opened}>
            {opened && (
              <div className={classes.responseTable}>
                <PropertyTable data={response} />
              </div>
            )}
          </Collapse>
        </>
      )}
    </motion.div>
  );
}
